import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SampleCensorSign, SampleCensorType } from 'src/app/enums/sampleCensorType.enum';
import { UnitSymbols } from 'src/app/enums/units.enum';
import { MassUnit } from 'src/app/enums/unitPreferences.enum';
import { IhLabResult } from 'src/app/models/IhLabResult.model';
import { GravimetricResultGroup, GravimetricResultVm } from 'src/app/models/gravimetric-result.model';
import { Parameter } from 'src/app/models/parameter.model';
import { JurisdictionRiskConfigService } from 'src/app/services/Jurisdiction-risk-config.service';
import { TenantRiskService } from 'src/app/services/tenantRisk.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { sanitizeCssColor } from 'src/app/shared/utils/css-color.util';

interface RiskLevel {
  Name: string;
  QuantThreshold_OEL: [number, number];
  MinSamples: number;
  Color: string;
}

@Component({
  standalone: false,
  selector: 'app-gravimetric-results',
  templateUrl: './gravimetric-results.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./gravimetric-results.component.scss']
})
export class GravimetricResultsComponent implements OnInit, OnChanges {
  @Input() iHLabResults: IhLabResult[] = [];
  @Input() isSampleTypeNoise: Boolean = false;
  @Input() isSampleTypeBiological: Boolean = false;
  @Input() showNoiseDoseComparison: boolean = true;

  /** Default gray color when no risk level threshold matches */
  private readonly DEFAULT_EXPOSURE_COLOR = '#828282';
  /** Gray color for results below Limit of Quantification */
  private readonly LOQ_GRAY_COLOR = '#9E9E9E';
  /** Green fallback for noise dose when no risk level matches */
  private readonly DEFAULT_NOISE_COLOR = '#4CAF50';

  filteredRisks: GravimetricResultVm[] = [];
  parameterWithDetails: Parameter;
  loadingSensorLabResults = true;
  /** Fallback risk levels from tenant config */
  private tenantRiskLevels: RiskLevel[] = [];
  /** Jurisdiction-specific risk levels: jurisdictionId -> riskLevels[] */
  private jurisdictionRiskLevelsMap: Map<string, RiskLevel[]> = new Map();
  groupedRisks: GravimetricResultGroup[] = [];

  constructor(
    private valueConversionService: ValueConversionService,
    private changeDetectorRef: ChangeDetectorRef,
    private tenantRiskService: TenantRiskService,
    private jurisdictionRiskConfigService: JurisdictionRiskConfigService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    this.tenantRiskService.getCurrent().subscribe({
        next: (result) => {
          try {
            this.tenantRiskLevels = result?.exposureLikelihoodLevels
              ? this.parseRiskLevelsFromJson(result.exposureLikelihoodLevels)
              : [];
          } catch {
            this.tenantRiskLevels = [];
          }
          if (this.iHLabResults?.length) {
            this.loadJurisdictionRiskConfigs();
          }
        },
        error: (error) => {
          this.toastService.showError(error);
          this.tenantRiskLevels = [];
          if (this.iHLabResults?.length) {
            this.loadJurisdictionRiskConfigs();
          }
        }
      });
  }

  ngOnChanges() {
    if (!this.iHLabResults?.length) {
      this.loadingSensorLabResults = false;
      return;
    }
    this.loadJurisdictionRiskConfigs();
  }

  /** Load jurisdiction-specific risk configs for all unique jurisdictions in results */
  private loadJurisdictionRiskConfigs() {
    const uniqueJurisdictionIds = [...new Set(
      this.iHLabResults
        .map(r => r.jurisdictionId)
        .filter(id => id != null)
    )];

    if (!uniqueJurisdictionIds.length) {
      this.setFilteredRisks();
      return;
    }

    const requests = uniqueJurisdictionIds.map(jId =>
      this.jurisdictionRiskConfigService.getByJurisdictionId(jId).pipe(
        catchError(() => of(null))  // Handle network errors
      )
    );

    forkJoin(requests).subscribe({
      next: (configs) => {
        configs.forEach((config, idx) => {
          // config can be null if jurisdiction has no risk config (not an error)
          if (config?.exposureLikelihoodLevels) {
            try {
              const levels = this.parseRiskLevelsFromJson(config.exposureLikelihoodLevels);
              this.jurisdictionRiskLevelsMap.set(uniqueJurisdictionIds[idx], levels);
            } catch {
            }
          }
        });
        this.setFilteredRisks();
      },
      error: () => {
        this.setFilteredRisks();
      }
    });
  }

  /** Get risk levels for a specific jurisdiction, falling back to tenant levels */
  private getRiskLevelsForJurisdiction(jurisdictionId: string | undefined): RiskLevel[] {
    if (jurisdictionId != null && this.jurisdictionRiskLevelsMap.has(jurisdictionId)) {
      return this.jurisdictionRiskLevelsMap.get(jurisdictionId);
    }
    return this.tenantRiskLevels;
  }

  private parseRiskLevelsFromJson(exposureLikelihoodLevelsJson: string): RiskLevel[] {
    const parsed = JSON.parse(exposureLikelihoodLevelsJson) as Record<string, unknown>;
    return Object.entries(parsed)
      .filter((entry): entry is [string, RiskLevel] => {
        const [key, value] = entry;
        if (key === 'ConsequenceRatings') return false;
        return typeof value === 'object' && value !== null && !Array.isArray(value);
      })
      .map(([_, value]) => value);
  }

  setFilteredRisks() {
    this.filteredRisks = [];
    const getUserPreferredValue = (value: number, agentUnit = 0) =>
      this.valueConversionService.getParameterValueAsPerAgentUnit(value, agentUnit);

    this.filteredRisks = this.iHLabResults.map((ihResult, i) => {
      const isLoq = ihResult.sampleCensor === SampleCensorType.LessThan;
      const oelValue = getUserPreferredValue(ihResult?.correctedOEL ?? ihResult?.oel, ihResult.agentUnits);
      const exposure = getUserPreferredValue(ihResult?.twa, ihResult.agentUnits);
      const lineScale = Math.max(oelValue, exposure);

      const rawSeverity = isLoq ? null :
        (ihResult.poel != null ? ihResult.poel :
          ((exposure == null || oelValue == null) ? null :
            (oelValue === 0 ? (exposure === 0 ? 0 : '∞') : (exposure / oelValue) * 100)));
      const severity = (rawSeverity != null && typeof rawSeverity === 'number') ? parseFloat(rawSeverity.toFixed(2)) : rawSeverity;

      // Use jurisdiction-specific risk levels for this result
      const riskLevels = this.getRiskLevelsForJurisdiction(ihResult.jurisdictionId);

      const shiftAdjustedDosePercentage = ihResult.correctedDosePercent ?? ihResult.dosePercentage;

      const filteredRisk: GravimetricResultVm = {
        analyteName: ihResult.agentName,
        oelValue,
        twa: exposure,
        oelLine: (exposure > 0 && exposure > oelValue) ? (oelValue / exposure) * 100 : null,
        Value: severity,
        Unit: ihResult.agentUnits != null ? UnitSymbols[ihResult.agentUnits] : '',
        exposureStyle: `width: ${lineScale > 0 ? (exposure / lineScale) * 100 : 0}%;`,
        exposureColor: isLoq ? this.LOQ_GRAY_COLOR : this.DEFAULT_EXPOSURE_COLOR,
        gravimetric: [],
        isFullWidth: i === this.iHLabResults.length - 1 && this.iHLabResults.length % 2 !== 0,
        jurisdictionId: ihResult.jurisdictionId,
        jurisdictionName: ihResult.jurisdictionName,
        sampleSign: ihResult.sampleCensor == SampleCensorType.NA ? '' : ihResult.sampleCensor != null ? SampleCensorSign[ihResult.sampleCensor] : SampleCensorSign[SampleCensorType.Equal],
        rawDosePercentage: ihResult.dosePercentage,
        shiftAdjustedDosePercentage,
        mass: ihResult.totalMass,
        massUnit: MassUnit[MassUnit.µg],
        isLoq,
        oelLabel: ihResult.isOelCorrectionApplied
          ? this.translateService.getDynamicTranslation('(Shift Adjusted)')
          : '',
        loqDisplayText: this.translateService.getDynamicTranslation('< Limit of Quantification'),
      };

      if (isLoq) return filteredRisk;

      riskLevels.forEach((threshold, index) => {
        if (!Array.isArray(threshold.QuantThreshold_OEL) || threshold.QuantThreshold_OEL.length < 2) {
          return;
        }
        const thresholdStart = (threshold.QuantThreshold_OEL[0] / 100) * oelValue;
        const thresholdEnd = (threshold.QuantThreshold_OEL[1] / 100) * oelValue;
        let thresholdWidth = this.calculateThresholdWidth(thresholdStart, thresholdEnd, lineScale);
        const safeColor = sanitizeCssColor(threshold.Color) || this.DEFAULT_NOISE_COLOR;

        filteredRisk.gravimetric.push({
          name: threshold.Name,
          value: thresholdWidth,
          color: safeColor,
          style: `width: ${thresholdWidth}%; background-image: linear-gradient(to right, ${safeColor} 80%, rgba(0, 0, 0, 0) 0%)`
        });

        if (!exposure) return;

        if (thresholdStart <= exposure && thresholdEnd >= exposure) {
          filteredRisk.exposureColor = safeColor;
        }
        if (index === riskLevels.length - 1 && exposure > thresholdEnd) {
          filteredRisk.exposureColor = safeColor;
        }
      });

      if (this.isSampleTypeNoise && shiftAdjustedDosePercentage != null) {
        const dose = shiftAdjustedDosePercentage;

        let matchedColor = null;
        riskLevels.forEach((threshold, index) => {
          if (!Array.isArray(threshold.QuantThreshold_OEL) || threshold.QuantThreshold_OEL.length < 2) return;

          const [start, end] = threshold.QuantThreshold_OEL;
          const safeNoiseColor = sanitizeCssColor(threshold.Color) || this.DEFAULT_NOISE_COLOR;
          if (dose >= start && dose <= end) {
            matchedColor = safeNoiseColor;
          }
          if (index === riskLevels.length - 1 && dose > end) {
            matchedColor = safeNoiseColor;
          }
        });
        filteredRisk.exposureColor = matchedColor ?? this.DEFAULT_NOISE_COLOR;
      }

      return filteredRisk;
    });
    if (this.filteredRisks.length) this.groupedRisks = this.groupByJurisdiction(this.filteredRisks);
    this.loadingSensorLabResults = false;
    this.changeDetectorRef.detectChanges();
  }

  private calculateThresholdWidth(thresholdStart: number, thresholdEnd: number, lineScale: number): number {
    if (thresholdStart > lineScale) {
      return 0;
    } else if (thresholdEnd > lineScale) {
      return ((lineScale - thresholdStart) / lineScale) * 100;
    } else {
      return ((thresholdEnd - thresholdStart) / lineScale) * 100;
    }
  }

  private groupByJurisdiction(results: GravimetricResultVm[]): GravimetricResultGroup[] {
    const groups: { [key: string]: GravimetricResultVm[] } = {};

    results.forEach(result => {
      const key = result.jurisdictionId ?? 'none';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(result);
    });

    return Object.keys(groups).map(key => {
      const first = groups[key][0];
      return {
        jurisdictionId: key === 'none' ? undefined : key,
        jurisdictionName: first?.jurisdictionName || this.translateService.getDynamicTranslation('No Jurisdiction'),
        results: groups[key]
      };
    });
  }

  public trackByIndex = trackByIndex;
}
